import React, { useEffect, useState } from "react";
import { Loader2, Truck, Phone, MapPin, Mail, Check, X, BadgeCheck, MessageCircle, Copy, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR, errMsg } from "@/lib/api";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const waLink = (num) => {
  const digits = (num || "").replace(/\D/g, "");
  const full = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${full}`;
};

const STATUSES = ["Order Confirmed", "Preparing", "Ready for Dispatch", "Shipped", "Out for Delivery", "Delivered"];
const badge = { "Order Confirmed": "bg-blue-100 text-blue-800", "Preparing": "bg-amber-100 text-amber-800", "Ready for Dispatch": "bg-purple-100 text-purple-800", "Shipped": "bg-indigo-100 text-indigo-800", "Out for Delivery": "bg-orange-100 text-orange-800", "Delivered": "bg-green-100 text-green-800", "pending_payment": "bg-gray-100 text-gray-700" };
const payBadge = { submitted: "bg-amber-100 text-amber-800", paid: "bg-green-100 text-green-800", failed: "bg-red-100 text-red-800" };

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [courierOpen, setCourierOpen] = useState(null);
  const [courier, setCourier] = useState({ courier_name: "", awb_number: "", tracking_url: "" });

  const load = () => { setLoading(true); api.get("/admin/orders").then((r) => { setOrders(r.data); setLoading(false); }); };
  useEffect(load, []);

  const changeStatus = async (order, status) => {
    try { await api.put(`/admin/orders/${order.order_id}/status`, { status }); toast.success(`Status updated to ${status}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const verifyPayment = async (order, action) => {
    try { await api.put(`/admin/orders/${order.order_id}/payment`, { action }); toast.success(action === "verify" ? "Payment verified — order confirmed" : "Payment rejected"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const openCourier = (o) => { setCourierOpen(o); setCourier({ courier_name: o.courier_name || "", awb_number: o.awb_number || "", tracking_url: o.tracking_url || "" }); };
  const saveCourier = async () => {
    try { await api.put(`/admin/orders/${courierOpen.order_id}/courier`, courier); toast.success("Courier details saved"); setCourierOpen(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const q = search.trim().toLowerCase();
  const filtered = orders
    .filter((o) => filter === "all" || (filter === "verify" ? o.payment_status === "submitted" : o.payment_status === "paid"))
    .filter((o) => !q || o.order_id.toLowerCase().includes(q) || (o.full_name || "").toLowerCase().includes(q));

  return (
    <AdminLayout title="Orders">
      <div className="relative mb-4">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          data-testid="order-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by Order ID or customer name..."
          className="w-full h-12 rounded-full bg-white border border-[#E5DFD3] pl-11 pr-4 outline-none focus:ring-2 focus:ring-gold/40"
        />
      </div>
      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar">
        {[["all", "All"], ["verify", "Awaiting Verification"], ["paid", "Confirmed"]].map(([k, l]) => (
          <button key={k} data-testid={`order-filter-${k}`} onClick={() => setFilter(k)} className={`shrink-0 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${filter === k ? "bg-choco text-cream" : "bg-white border border-[#E5DFD3] text-chocodark"}`}>{l}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 size={36} className="animate-spin text-goldsoft" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24 text-muted-foreground">No orders here yet.</div>
      ) : (
        <div className="space-y-4">
          {filtered.map((o) => (
            <div key={o.id} data-testid={`admin-order-${o.order_id}`} className="bg-white rounded-2xl border border-[#E5DFD3] p-5 sm:p-6">
              <div className="flex flex-wrap justify-between gap-3 items-start">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-xl font-bold text-choco">{o.order_id}</span>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${badge[o.status] || "bg-gray-100"}`}>{o.status === "pending_payment" ? "Awaiting verification" : o.status}</span>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize ${payBadge[o.payment_status] || "bg-gray-100"}`}>Payment: {o.payment_status}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{new Date(o.created_at).toLocaleString("en-IN")}</p>
                  {o.payment_reference && <p className="text-xs text-muted-foreground">Ref: <span className="font-medium text-choco">{o.payment_reference}</span></p>}
                </div>
                <span className="font-display text-2xl font-bold text-choco">{formatINR(o.total)}</span>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mt-4 text-sm">
                <div className="space-y-1">
                  <p className="font-semibold text-choco">{o.full_name}</p>
                  <p className="flex items-center gap-2 text-muted-foreground"><Phone size={14} /> {o.phone}</p>
                  <div className="flex gap-2 mt-1">
                    <button data-testid={`copy-phone-${o.order_id}`} onClick={() => { navigator.clipboard.writeText(o.phone); toast.success("Number copied"); }} className="inline-flex items-center gap-1 text-xs rounded-full bg-mutedcream px-3 py-1.5 text-choco hover:bg-[#E5DFD3]"><Copy size={13} /> Copy Number</button>
                    <a data-testid={`whatsapp-${o.order_id}`} href={waLink(o.phone)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs rounded-full bg-green-600 text-white px-3 py-1.5 hover:bg-green-700"><MessageCircle size={13} /> Open WhatsApp</a>
                  </div>
                  <p className="flex items-center gap-2 text-muted-foreground"><Mail size={14} /> {o.email}</p>
                  <p className="flex items-start gap-2 text-muted-foreground"><MapPin size={14} className="mt-0.5" /> {o.address}, {o.city}, {o.state} - {o.pincode}</p>
                  {o.delivery_instructions && <p className="text-xs italic text-muted-foreground">Note: {o.delivery_instructions}</p>}
                </div>
                <div className="bg-cream rounded-xl p-3 space-y-1">
                  {o.items.map((it) => (<div key={it.product_id} className="flex justify-between text-sm"><span className="text-chocodark">{it.name} × {it.quantity}</span><span className="font-medium">{formatINR(it.line_total)}</span></div>))}
                  <div className="border-t border-[#E5DFD3] pt-1 flex justify-between text-xs text-muted-foreground"><span>Delivery</span><span>{o.delivery_charge === 0 ? "FREE" : formatINR(o.delivery_charge)}</span></div>
                </div>
              </div>

              {o.payment_status === "submitted" && (
                <div className="flex flex-wrap gap-2 mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 items-center">
                  <span className="text-sm font-medium text-amber-900 mr-1">Verify payment (Ref: {o.payment_reference || "—"}):</span>
                  <button data-testid={`verify-payment-${o.order_id}`} onClick={() => verifyPayment(o, "verify")} className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full font-medium bg-green-600 text-white hover:bg-green-700 min-h-[40px]"><Check size={15} /> Mark Paid</button>
                  <button data-testid={`reject-payment-${o.order_id}`} onClick={() => verifyPayment(o, "reject")} className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full font-medium bg-white border border-red-300 text-red-700 hover:bg-red-50 min-h-[40px]"><X size={15} /> Reject</button>
                </div>
              )}

              {o.payment_status === "paid" && (
                <div className="flex flex-wrap gap-2 mt-5 items-center">
                  <span className="text-sm font-medium text-choco mr-1">Update status:</span>
                  {STATUSES.map((s) => (
                    <button key={s} data-testid={`status-btn-${o.order_id}-${s.replace(/\s/g, "-")}`} onClick={() => changeStatus(o, s)} className={`text-xs px-3 py-2 rounded-full font-medium transition-colors min-h-[40px] ${o.status === s ? "bg-choco text-cream" : "bg-mutedcream text-chocodark hover:bg-[#E5DFD3]"}`}>{s}</button>
                  ))}
                  <button data-testid={`courier-btn-${o.order_id}`} onClick={() => openCourier(o)} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full font-medium bg-white border border-[#E5DFD3] text-choco hover:border-goldsoft min-h-[40px]"><Truck size={14} /> Courier</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!courierOpen} onOpenChange={(v) => !v && setCourierOpen(null)}>
        <DialogContent className="bg-cream">
          <DialogHeader><DialogTitle className="font-display text-2xl text-choco">Courier & Tracking</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium text-choco">Courier Name</label><Input data-testid="courier-name" value={courier.courier_name} onChange={(e) => setCourier({ ...courier, courier_name: e.target.value })} className="mt-1 h-11 bg-white border-[#E5DFD3]" /></div>
            <div><label className="text-sm font-medium text-choco">AWB Number</label><Input data-testid="courier-awb" value={courier.awb_number} onChange={(e) => setCourier({ ...courier, awb_number: e.target.value })} className="mt-1 h-11 bg-white border-[#E5DFD3]" /></div>
            <div><label className="text-sm font-medium text-choco">Tracking URL</label><Input data-testid="courier-url" value={courier.tracking_url} onChange={(e) => setCourier({ ...courier, tracking_url: e.target.value })} placeholder="https://..." className="mt-1 h-11 bg-white border-[#E5DFD3]" /></div>
            <button data-testid="courier-save" onClick={saveCourier} className="w-full rounded-xl bg-choco text-cream font-semibold px-6 py-3.5 hover:bg-chocodark transition-colors">Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminOrders;
